import React from 'react';
import { Shield, Lock, CreditCard, Landmark, FileText, CheckCircle2, AlertCircle, ArrowLeft, Download, Mail, Phone, MapPin } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { API_URL as API } from '../config/api';

export default function CustomerAgreement() {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#F8FAF7', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", paddingBottom: '4rem' }}>
      {/* Header Banner */}
      <div style={{
        background: '#084734',
        color: 'white',
        padding: '3.5rem 2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'rgba(206, 241, 123, 0.08)',
          pointerEvents: 'none'
        }} />
        <div style={{ maxWidth: '840px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(206, 241, 123, 0.15)',
            border: '1px solid rgba(206, 241, 123, 0.3)',
            padding: '6px 16px',
            borderRadius: '50px',
            color: '#CEF17B',
            fontSize: '0.85rem',
            fontWeight: '700',
            marginBottom: '1rem'
          }}>
            <Shield size={16} color="#CEF17B" /> Customer Legal & Privacy Framework
          </div>
          <h1 style={{ fontSize: '2.4rem', fontWeight: '900', margin: '0 0 0.75rem 0', letterSpacing: '-0.5px' }}>
            Customer Service & Data Privacy Agreement
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1rem', lineHeight: '1.6', margin: 0 }}>
            Master Terms for TurfX Players, Slot Reservations, Data Processing & Financial Security
          </p>
        </div>
      </div>

      {/* Main Content Container */}
      <div style={{ maxWidth: '920px', margin: '-2rem auto 0 auto', padding: '0 1.5rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <BackButton />
        </div>

        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '2.5rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
          border: '1.5px solid #EEF2E6'
        }}>
          {/* Quick summary notice */}
          <div style={{
            background: '#F0FDF4',
            border: '1.5px solid #BBF7D0',
            borderRadius: '16px',
            padding: '1.25rem 1.5rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontWeight: '800', fontSize: '0.95rem' }}>
                <CheckCircle2 size={18} /> Digitally Enforceable Master Agreement
              </div>
              <p style={{ margin: '4px 0 0 0', color: '#15803D', fontSize: '0.85rem', lineHeight: 1.5 }}>
                By registering an account on TurfX, you provide affirmative electronic consent to these terms.
              </p>
            </div>
            <a
              href={`${API}/agreements/download/template_customer`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: '#084734',
                color: '#CEF17B',
                padding: '8px 16px',
                borderRadius: '10px',
                fontSize: '0.82rem',
                fontWeight: '800',
                textDecoration: 'none'
              }}
            >
              <Download size={14} /> Download Sample PDF
            </a>
          </div>

          {/* Section 1 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FileText size={20} color="#084734" /> 1. Purpose, Scope & Consent to Agreement
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', margin: 0 }}>
              This Customer Service and Data Privacy Agreement ("Agreement") forms a legally binding electronic contract between you ("Customer", "Player", or "User") and <strong>TurfX</strong> (operated by <strong>MetaQode Technologies Pvt. Ltd.</strong>). By registering an account, reserving sports facilities, or using any TurfX applications, you provide affirmative electronic consent under Section 4 and Section 10A of the Information Technology Act, 2000 (India).
            </p>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 2 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Shield size={20} color="#084734" /> 2. Personal Data We Collect & Data Categories
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', marginBottom: '1rem' }}>
              We collect and process only the minimum necessary information required to facilitate venue reservations, match scheduling, payment processing, and safety compliance:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.2rem', borderRadius: '14px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800', fontSize: '0.92rem' }}>👤 Identity & Account Details</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B', lineHeight: '1.6' }}>
                  Full name, mobile number verified via OTP, active email address, salted password hash, and optional player profile photograph.
                </p>
              </div>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.2rem', borderRadius: '14px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800', fontSize: '0.92rem' }}>📅 Reservation & Match Pass History</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B', lineHeight: '1.6' }}>
                  Selected sports disciplines, reserved venue courts, slot timings, QR match passes, gate attendance check-ins, and match scorecards.
                </p>
              </div>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.2rem', borderRadius: '14px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800', fontSize: '0.92rem' }}>💳 Encrypted Transaction Identifiers</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B', lineHeight: '1.6' }}>
                  Encrypted payment gateway reference codes and transaction order IDs. Raw card numbers, CVVs, and netbanking credentials are NEVER stored.
                </p>
              </div>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.2rem', borderRadius: '14px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800', fontSize: '0.92rem' }}>🏦 Bank / UPI Details (Refunds)</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B', lineHeight: '1.6' }}>
                  Bank account number, account holder name, IFSC code, or UPI ID stored in encrypted format exclusively to disburse instant refunds and wallet withdrawals.
                </p>
              </div>
            </div>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 3 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CheckCircle2 size={20} color="#084734" /> 3. Why We Collect Data & How It Is Used
            </h2>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', paddingLeft: '1.5rem', margin: 0 }}>
              <li><strong>Slot Reservation & Gate Pass Generation:</strong> Real-time locking of venue court slots, generation of encrypted QR entry passes, and gatekeeper check-in validation.</li>
              <li><strong>Secure Payment Gateway Routing:</strong> Transmitting order totals to RBI-authorized encrypted payment gateways and calculating statutory GST invoices.</li>
              <li><strong>Customer Support & Disputed Refunds:</strong> Reviewing venue attendance records, resolving cancellation claims, and managing rainout weather reschedules.</li>
              <li><strong>Platform Security & Legal Compliance:</strong> Maintaining immutable transaction audit logs complying with the Information Technology Act (2000) and CERT-In guidelines.</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 4 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CreditCard size={20} color="#084734" /> 4. Payment Gateway Security & Encrypted Processing
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              All monetary transactions on TurfX are processed securely through RBI-authorized, PCI-DSS Level 1 compliant, 256-bit SSL encrypted payment gateways.
            </p>
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', padding: '1.25rem', borderRadius: '14px', color: '#92400E', fontSize: '0.9rem', lineHeight: '1.6' }}>
              <strong>🔒 Strict Data Minimization & Card Tokenization:</strong> TurfX strictly complies with Reserve Bank of India (RBI) card tokenization directives. We NEVER capture, record, or store full credit/debit card numbers, CVVs, expiry dates, or netbanking passwords on any internal database or server.
            </div>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 5: 5% Customer Platform Commission & Tournament Entry Fees */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Lock size={20} color="#084734" /> 5. 5% Customer Platform Commission & Tournament Entry Fees
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              TurfX maintains transparent pricing with standard technology facilitation charges:
            </p>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', paddingLeft: '1.5rem', margin: 0 }}>
              <li><strong>5% Customer Booking Commission:</strong> TurfX charges a <strong>5% platform convenience commission</strong> (+ applicable GST) on each court slot booking to support real-time court availability locks, SMS/WhatsApp alerts, and secure gateway routing.</li>
              <li><strong>Tournament Registration Fees:</strong> For tournament registrations, participating players and teams pay the specified tournament entry fee plus a <strong>5% tournament facilitation commission</strong> covering online bracket draws, live scorecards, match fixtures, and verified prize pool payouts.</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 6 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Landmark size={20} color="#084734" /> 6. Bank Details Collection Policy for Players
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              Why do we collect player bank details? Customers may optionally link their bank account (Account Number, IFSC, Name) or UPI VPA for the following explicit purposes:
            </p>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', paddingLeft: '1.5rem', margin: 0 }}>
              <li><strong>Instant Direct-to-Bank Refunds:</strong> If a booking is cancelled due to adverse weather conditions, floodlight outages, or partner unavailability, you can choose to transfer funds directly to your bank account instead of platform wallet credits.</li>
              <li><strong>Tournament Prizes & Wallet Cash-Out:</strong> Any tournament prize pools or promotional referral cash-outs earned on the TurfX platform can be withdrawn directly to your savings/current account.</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 7 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CheckCircle2 size={20} color="#084734" /> 7. Booking, Cancellation, Rescheduling & Refund Rules
            </h2>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', paddingLeft: '1.5rem', margin: 0 }}>
              <li><strong>Cancellations More Than 6 Hours Prior (&gt; 6 hrs):</strong> You are eligible for a 100% refund of the base court fee credited to your TurfX wallet or source account.</li>
              <li><strong>Cancellations Under 6 Hours Prior (&lt; 6 hrs):</strong> Non-refundable to compensate the venue partner for court reservation blocking and operational preparedness.</li>
              <li><strong>Rescheduling Policy:</strong> A one-time date/time slot rescheduling is permitted up to 6 hours prior to the match, subject to venue availability and court pricing differentials.</li>
              <li><strong>Weather Disruptions:</strong> In case of torrential rain or natural force majeure, match passes are either rescheduled at zero extra cost or refunded upon venue confirmation.</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 8 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Shield size={20} color="#084734" /> 8. Player Code of Conduct, Safety & Ground Rules
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              All players entering sports turf premises agree to comply with venue ground rules:
            </p>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', paddingLeft: '1.5rem', margin: 0 }}>
              <li><strong>Footwear Guidelines:</strong> Players must wear approved footwear (rubber stud turf shoes, non-marking indoor court shoes). Metal spikes are strictly banned on artificial turf.</li>
              <li><strong>Zero-Tolerance on Misconduct:</strong> Violence, foul language, harassment, smoking, consumption of alcohol, or illegal substances is strictly prohibited on turf premises.</li>
              <li><strong>Punctuality & Overstay:</strong> Players must vacate the court promptly when their booked slot duration ends to respect subsequent player bookings.</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 9 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FileText size={20} color="#084734" /> 9. Limitation of Liability, Governing Law & Jurisdiction
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              TurfX functions as a technology facilitator and intermediary under Section 79 of the Information Technology Act, 2000. Physical sports activities involve inherent risk of bodily injury. By using the platform, players assume full voluntary risk for their physical wellbeing. TurfX and MetaQode Technologies Pvt. Ltd. shall not be liable for on-court injuries or lost personal belongings. This contract is governed by the laws of India, with exclusive dispute jurisdiction in courts of Karnataka.
            </p>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 10: Policy Links */}
          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem' }}>
              10. Incorporation of Privacy Policy & Terms of Service
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              This Customer Agreement explicitly incorporates our complete platform policies:
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <Link
                to="/privacy-policy"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#F8FAF7',
                  border: '1.5px solid #084734',
                  color: '#084734',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '0.88rem',
                  textDecoration: 'none'
                }}
              >
                📄 See Full Privacy Policy →
              </Link>
              <Link
                to="/terms-and-conditions"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#F8FAF7',
                  border: '1.5px solid #084734',
                  color: '#084734',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '0.88rem',
                  textDecoration: 'none'
                }}
              >
                📋 See Terms & Conditions →
              </Link>
            </div>
          </section>

          {/* Electronic Acknowledgment & Consent Box */}
          <div style={{
            background: '#F0FDF4',
            border: '2px solid #86EFAC',
            borderRadius: '18px',
            padding: '1.5rem 1.75rem',
            marginTop: '2rem',
            boxShadow: '0 4px 15px rgba(22, 163, 74, 0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#166534', fontWeight: '900', fontSize: '1.05rem', marginBottom: '8px' }}>
              <CheckCircle2 size={22} color="#16a34a" /> Electronic Consent & Acceptance Statement
            </div>
            <p style={{ margin: '0 0 10px 0', color: '#15803D', fontSize: '0.98rem', fontWeight: '700', lineHeight: '1.6' }}>
              "I have read and acknowledge that I accept the above terms and conditions."
            </p>
            <p style={{ margin: 0, color: '#166534', fontSize: '0.82rem', lineHeight: '1.5' }}>
              By registering an account, booking sports venues, or submitting details on TurfX, this electronic acceptance is legally recognized and enforceable under the Information Technology Act, 2000 (India).
            </p>
          </div>

          {/* MetaQode Address & Contact Details Section */}
          <div style={{ background: '#084734', color: 'white', borderRadius: '18px', padding: '1.75rem 2rem', display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '10px' }}>
              <div style={{ fontWeight: '900', fontSize: '1.05rem', color: '#CEF17B' }}>
                MetaQode Technologies Pvt. Ltd.
              </div>
              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', padding: '3px 10px', borderRadius: '6px' }}>
                Parent & Operating Company of TurfX
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginTop: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.88rem', color: '#E2E8F0', lineHeight: 1.5 }}>
                <MapPin size={18} color="#CEF17B" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong style={{ color: 'white' }}>Registered Corporate Office:</strong><br />
                  MetaQode Technologies Pvt. Ltd.,<br />
                  Chitradurga - 577501, Karnataka, India
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', color: '#E2E8F0' }}>
                  <Mail size={16} color="#CEF17B" />
                  <span>Support Email: <a href="mailto:support@metaqode.co.in" style={{ color: '#CEF17B', textDecoration: 'none', fontWeight: '600' }}>support@metaqode.co.in</a></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', color: '#E2E8F0' }}>
                  <Phone size={16} color="#CEF17B" />
                  <span>Helpline Phone: <strong style={{ color: 'white' }}>+91 9980998199</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', color: '#E2E8F0' }}>
                  <Shield size={16} color="#CEF17B" />
                  <span>Official Portal: <a href="https://metaqode.co.in" target="_blank" rel="noreferrer" style={{ color: '#CEF17B', textDecoration: 'none', fontWeight: '600' }}>https://metaqode.co.in</a></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
